import React from 'react';
import { getDefaultChainColor, getDefaultPocketColor } from '../../contexts/ControlTypes';
import { render_chain_ids } from '../../helpers/TableHelper';
import { ApoHoloResultRow } from '../../models/ApoHoloResultRow';
import {
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';
import { AhojTableBase } from './AhojTableBase';
import ColorPairButton from './ColorPairButton';
import StructureRepresentationControlComponent from './StructureRepresentationControlComponent';

/**
 * Table showing **AlphaFold** predicted structure matches. Uses the
 * `ViewerAutoPreset` (pLDDT coloring) instead of the default `NoWaterPreset`
 * so the confidence score is immediately visible. The Representation type is
 * initialized to 'cartoon' (same default as holo/apo chains).
 *
 * Because AlphaFold models have no experimental ligands, the ligand controls in
 * the per-row edit panel are hidden (same as apo structures).
 */
class AlphaFoldModelTable extends AhojTableBase {
   render_alphafolddb_ids = (uniprot_ids: string[]): React.JSX.Element => {
      return (
         <>
            {uniprot_ids.map((uniprot_id, index) => (
               <React.Fragment key={uniprot_id}>
                  <span className="uniprot-id">{uniprot_id}</span>
                  {index < uniprot_ids.length - 1 ? ',' : ''}
               </React.Fragment>
            ))}
         </>
      );
   };

   render(): React.JSX.Element {
      const sortedData = this.getSortedData(this.props.data.found_alphafold);
      const exp = this.expandedColumns;
      // DEFAULT: 10 cols — AlphaFold model | AT | Lig | pRMSD | SASA | 👁 | ✏ | Seq | ℹ | 🎨
      // EXPANDED: 14 cols — + MBR | RMSD | Dis | TM | Vol
      const colSpan = exp ? 14 : 10;
      return (
         <div className="found-struct-box">
            <div className="found-struct-table scrollable-table">
               <table id="af-sites">
                  <thead>
                     <tr>
                        <th data-tooltip="Matching structure (AF_ID/chains)">AlphaFold model</th>
                        <th
                           data-tooltip={`Aligned to chain(s) of query structure (${this.props.data.target_pdb_id}). *query POI on hover.`}
                           className="at"
                        >
                           AT
                        </th>
                        {exp && (
                           <>
                              <th
                                 data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure (#resis3 in results). *MBR-SEQRES on hover. Click to sort."
                                 className="so"
                                 style={this.sortableThStyle}
                                 onClick={(): void => this.handleSort('mapped_binding_residues_observed')}
                              >
                                 MBR{this.sortIndicator('mapped_binding_residues_observed')}
                              </th>
                              <th
                                 data-tooltip="RMSD value from the structural alignment (MM-align) of relevant chains (applies to Ca atoms). *number of aligned residues on hover. Click to sort."
                                 style={this.sortableThStyle}
                                 onClick={(): void => this.handleSort('rmsd')}
                              >
                                 RMSD{this.sortIndicator('rmsd')}
                              </th>
                              <th
                                 data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure. Click to sort."
                                 style={this.sortableThStyle}
                                 onClick={(): void => this.handleSort('mapped_binding_residues_unobserved')}
                              >
                                 Dis{this.sortIndicator('mapped_binding_residues_unobserved')}
                              </th>
                           </>
                        )}
                        <th data-tooltip="Ligands present in query pocket or mapped candidate pocket.">Lig</th>
                        <th
                           data-tooltip="Pocket RMSD: all-atom RMSD from the sequence-based pocket alignment (0 refinement cycles). Click to sort."
                           style={this.sortableThStyle}
                           onClick={(): void => this.handleSort('pocket_rmsd')}
                        >
                           pRMSD{this.sortIndicator('pocket_rmsd')}
                        </th>
                        <th
                           data-tooltip="Solvent Accessible Surface Area of pocket residues (UniProt SEQRES observed). Click to sort."
                           style={this.sortableThStyle}
                           onClick={(): void => this.handleSort('sasa')}
                        >
                           SASA{this.sortIndicator('sasa')}
                        </th>
                        {exp && (
                           <>
                              <th
                                 data-tooltip="TM-score 1 from the structural alignment (MM-align) of the relevant candidate chains onto the relevant query chains. Click to sort."
                                 style={this.sortableThStyle}
                                 onClick={(): void => this.handleSort('tm_score')}
                              >
                                 TM{this.sortIndicator('tm_score')}
                              </th>
                              <th data-tooltip="Pocket volume calculated by PyVOL (default settings).">Vol</th>
                           </>
                        )}
                        <th data-tooltip="Show/hide first 20 structures">
                           <button
                              className="btn-showhideall"
                              style={{ width: '1em' }}
                              onClick={(): void => this.showHideAll(sortedData)}
                           >
                              <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                           </button>
                        </th>
                        <th data-tooltip="Edit representation">&#9998;</th>
                        <th data-tooltip="Add/remove from Sequence Viewer">Seq</th>
                        <th
                           data-tooltip={exp ? 'Collapse to default columns' : 'Expand to show all columns'}
                           style={{ cursor: 'pointer' }}
                           className={exp ? 'active' : ''}
                           onClick={(): void => this.toggleExpandedColumns()}
                        >
                           ℹ
                        </th>
                        <th data-tooltip="Quick color — click a row to apply a unique color pair">
                           {this.hasAnyColorPairActive(sortedData) ? (
                              <button title="Reset all colors" onClick={(e): void => this.resetAllColors(e)}>
                                 &#8635;
                              </button>
                           ) : (
                              <span style={{ opacity: 0.35, fontSize: '0.85em' }}>◉</span>
                           )}
                        </th>
                     </tr>
                  </thead>
                  <tbody>
                     <>
                        {sortedData.map((af_model: ApoHoloResultRow) => (
                           <React.Fragment key={af_model.row_id}>
                              <tr
                                 data-row-id={af_model.row_id}
                                 onClick={(): void => this.selectUnselectStructure(af_model)}
                                 className={`clickable-row ${this.context.controlsMap.get(af_model.row_id)?.isSelected ? 'selected-row' : ''}`}
                              >
                                 <td>
                                    <a title="UniProt IDs used to build the AlphaFold amodel">
                                       {this.render_alphafolddb_ids(af_model.uniprot_ids)}
                                    </a>
                                    &nbsp;<span className="slash">/</span>&nbsp;
                                    <span title="Query chains with a corresponding AlphaFold model">
                                       {render_chain_ids(af_model.chains)}
                                    </span>
                                 </td>
                                 <td title={af_model.target_poi_alt}>{render_chain_ids(af_model.target_chains)}</td>
                                 {exp && (
                                    <>
                                       <td
                                          title={`MBR-SEQRES: ${af_model.mapped_binding_residues_num ?? 0} (${af_model.mapped_binding_residues_percent ?? 0}%)`}
                                       >
                                          {af_model.mapped_binding_residues_observed}
                                       </td>
                                       <td title={`aligned length: ${af_model.aln_len}`}>{af_model.rmsd}</td>
                                       <td
                                          title={`% unobserved: ${af_model.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                       >
                                          {af_model.mapped_binding_residues_unobserved ?? '–'}
                                       </td>
                                    </>
                                 )}
                                 <td>{af_model.ligands ? af_model.ligands.join(' ') : '–'}</td>
                                 <td title={`aligned length: ${af_model.pocket_len}`}>{af_model.pocket_rmsd ?? '–'}</td>
                                 <td title={`avg. SASA: ${af_model.sasa_avg ?? 0}`}>{af_model.sasa ?? '–'}</td>
                                 {exp && (
                                    <>
                                       <td title={`inverse TM-score: ${af_model.tm_score_inv ?? 0}`}>
                                          {af_model.tm_score ?? '–'}
                                       </td>
                                       <td>{af_model.volume ?? '–'}</td>
                                    </>
                                 )}
                                 <td>
                                    <button
                                       className={`btn-showhide${this.context.controlsMap.get(af_model.row_id)?.isVisible ? '' : ' hidden'}`}
                                       style={{ width: '1em' }}
                                       title="show / hide"
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.showHideStructure(af_model);
                                       }}
                                    >
                                       <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title="edit"
                                       onClick={(e): void => this.toggleEditPanel(af_model.row_id, e)}
                                       style={{ cursor: 'pointer' }}
                                    >
                                       {this.editingRowId === af_model.row_id ? '▼' : '▶'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title={
                                          this.context.selectedPdb.some((s) => s.rowId === af_model.row_id)
                                             ? 'Remove from Sequence Viewer'
                                             : 'Add to Sequence Viewer'
                                       }
                                       className={`btn-seq${this.context.selectedPdb.some((s) => s.rowId === af_model.row_id) ? ' active' : ''}`}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.addOrRemoveFromRcsb(af_model);
                                       }}
                                    >
                                       {this.context.selectedPdb.some((s) => s.rowId === af_model.row_id) ? '−' : '+'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       className={`btn-info${this.infoRowId === af_model.row_id ? ' active' : ''}`}
                                       title="Show info"
                                       onClick={(e): void => this.toggleInfoPanel(af_model.row_id, e)}
                                    >
                                       ℹ
                                    </button>
                                 </td>
                                 <td className="color-pair-cell">
                                    <ColorPairButton
                                       pocketColor={
                                          this.context.controlsMap.get(af_model.row_id)?.pocketColor ??
                                          getDefaultPocketColor(this.props.structureType)
                                       }
                                       chainColor={
                                          this.context.controlsMap.get(af_model.row_id)?.chainColor ??
                                          getDefaultChainColor(this.props.structureType)
                                       }
                                       active={this.isColorPairActive(af_model.row_id)}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.selectNextColorPair(af_model);
                                       }}
                                    />
                                 </td>
                              </tr>
                              {this.editingRowId === af_model.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="edit-panel-cell">
                                       <div className="edit-panel-body">
                                          <div className="edit-panel-controls">
                                             <StructureRepresentationControlComponent
                                                mode="big"
                                                values={{
                                                   chain: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(af_model.row_id)
                                                            ?.selectedRepresentations.get('chain') ??
                                                         defaultRestRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(af_model.row_id)?.chainColor ??
                                                         getDefaultChainColor(this.props.structureType),
                                                   },
                                                   pocket: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(af_model.row_id)
                                                            ?.selectedRepresentations.get('pocket') ??
                                                         defaultPocketRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(af_model.row_id)?.pocketColor ??
                                                         getDefaultPocketColor(this.props.structureType),
                                                   },
                                                }}
                                                onRepresentationChange={(
                                                   target: StructureComponentType,
                                                   type: Representation,
                                                ): void => this.changeRepresentation(type, af_model, target)}
                                                onColorChange={(target: StructureComponentType, color: string): void =>
                                                   this.changeColor(af_model, color, target)
                                                }
                                             />
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                              {this.infoRowId === af_model.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="info-panel-cell">
                                       <div className="info-panel-body">
                                          <div className="info-panel-grid">
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure."
                                                >
                                                   MBR
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`MBR-SEQRES: ${af_model.mapped_binding_residues_num ?? 0} (${af_model.mapped_binding_residues_percent ?? 0}%)`}
                                                >
                                                   {af_model.mapped_binding_residues_observed ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure."
                                                >
                                                   Dis
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`% unobserved: ${af_model.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                                >
                                                   {af_model.mapped_binding_residues_unobserved ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Pocket volume calculated by PyVOL (default settings)."
                                                >
                                                   Vol
                                                </span>
                                                <span className="info-panel-value">{af_model.volume ?? '–'}</span>
                                             </div>
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                           </React.Fragment>
                        ))}
                     </>
                  </tbody>
               </table>
            </div>
         </div>
      );
   }
}

export default AlphaFoldModelTable;
